import React, { useCallback, useMemo } from 'react'
import { Box, CircularProgress, Stack, Typography, useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'

import { BEIGE_300, BEIGE_500, BEIGE_600, BEIGE_700, BOX_SHADOW_XS, GRAY_500, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import type { CartItemId } from 'models/purchaseFlowRevamp'
import { formatPrice } from 'utils/price/priceFormatter'

import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'
import { useActionPopup } from 'utils/hooks'
import { usePfVatRate } from 'dataQueries/purchaseFlowRevampVat.query'
import { usePfStore } from '../../store'
import { PF_CART_HIDDEN_QUERY } from '../StepShell.component'
import { CartItemCard } from './CartItemCard.component'
import { CartExtraCard } from './CartExtraCard.component'
import { PackCartGroup } from './PackCartGroup.component'
import { groupRowsByPack } from './packGroups'
import Big from 'big.js'
import { MeasurementBreakdown } from './MeasurementBreakdown.component'
import { CartSummary } from './CartSummary.component'
import { StepNavSection, type StepNavStep } from './StepNavSection.component'
import { useCartDisplay } from './useCartDisplay'
import { oneSystemCartSummary } from '../../store/oneSystemPricing'
import { PfRevampEventName, usePfRevampAnalytics } from '../../analytics'
import { PF_STICKY_TOP } from '../stickyOffsets'
import { usePfEmbed } from '../../routing/usePfEmbed'
import { useRightRailFooterRef } from 'utils/hooks/rightRailRegistry'
import { useOrderExtras } from '../../store/stagingRenovation'

/**
 * Bottom breathing room so the cart's Total row + Proceed CTA never sit flush
 * against the viewport bottom (F17). Phase C-B: applies on every step now —
 * the page-level sticky Back/Next footer is gone, the cart sidebar owns nav.
 */

/**
 * Right-hand cart panel for the PF revamp step pages (Figma 2725:9156 "Summary",
 * 2725:7311 anatomy).
 *
 * Layout:
 *   - Outer container: sticky to viewport with full available column height
 *   - Scroll region (flex: 1): property indicator + summary title + cart items
 *   - Pinned bottom bar: totals (CartSummary) + Proceed CTA, separated by a
 *     top border — creates the "sticky bottom bar" feel from the Figma spec
 *
 * F1 — Property enforcement: when no property is selected and the user clicks
 * Proceed, the property-selection drawer opens instead of navigating forward.
 * The CTA tooltip surfaces this requirement proactively.
 *
 * F2 — Property indicator: a beige pinned card at the top shows the selected
 * property address when one is present (Figma 3977:50131).
 *
 * Edit handoff: clicking a row calls `openForEdit(cartItemId, snapshot)` on
 * the drawer slice — the ProductDrawer picks up the state change and opens
 * itself. No prop threading required.
 */
export const CartSidebar: React.FC = () => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  // Distinguishes the two render contexts: desktop sticky sidebar (false) vs the
  // mobile cart sheet inside MobileBottomBar's drawer (true). Drives the
  // StepNavSection mobile variant (generic "Proceed to next step" label +
  // inline disabled-reason, since touch can't surface the tooltip).
  // The cart's own chrome follows the rail, not the top bar's mobile query —
  // between the two the cart renders inside the drawer, which owns the title.
  const isMobile = useMediaQuery(PF_CART_HIDDEN_QUERY)
  const footerRef = useRightRailFooterRef<HTMLDivElement>()

  const items = usePfStore((s) => s.items)
  const isPaymentPending = usePfStore((s) => s.paymentFailure !== null)
  const removeItem = usePfStore((s) => s.removeItem)
  // The configuration route is `configuration/:cartItemId/:flowId`; on every
  // other step the param is simply absent and nothing is highlighted.
  const { cartItemId: activeCartItemId } = useParams<{ cartItemId?: string }>()
  const openForEdit = usePfStore((s) => s.openForEdit)

  const property = usePfStore((s) => s.property)
  const ctBookingName = usePfStore((s) => (s.ctBookingId ? s.ctBookingName : undefined))
  const { isEmbed } = usePfEmbed()
  // Only reaches the VAT row while no rate is known.
  const paymentMethod = usePfStore((s) => s.paymentMethod)
  // What the Stripe intent will charge, for CartSummary's parity tripwire. Withheld while a re-price is
  // in flight or has failed: the cart has already moved and the old amount is stale, not a mismatch.
  const intentAmount = usePfStore((s) => s.intentAmount)
  const isIntentSyncing = usePfStore((s) => s.isIntentSyncing)
  const isIntentFailed = usePfStore((s) => s.isIntentFailed)
  const chargedAmount = isIntentSyncing || isIntentFailed ? null : intentAmount
  // Idle until an invoicing address is chosen, so this fires on Billing and
  // after, never on the catalogue step.
  const vatQuery = usePfVatRate()
  const catalogueQuery = usePfCatalogue()

  // Read currentStep from the store instead of deriving it from 5
  // useMatch() calls. Wave 1 made `useSyncStepWithRoute` the canonical writer
  // of `currentStep`; reading it here eliminates the router-based duplication
  // and is cheaper (one subscription vs. 5 route comparisons per render).
  const navStep: StepNavStep = usePfStore((s) => s.currentStep)

  // Phase E-A.7 — order-level instruction extras flow into the cart total
  // via the bridge. Subscribe so the sidebar updates live when the user
  // toggles "Fast delivery (+€39)" inside the legacy Instructions form.
  const extrasFromInstructions = usePfStore((s) => s.extrasFromInstructions)
  const oneSystem = useMemo(() => oneSystemCartSummary(items, extrasFromInstructions), [items, extrasFromInstructions])

  const extras = useOrderExtras(catalogueQuery.data)
  const { rows, extraRows, total, currency } = useCartDisplay(items, catalogueQuery.data, extras)

  // Names come from the catalogue, so a cart that has lines but no catalogue
  // yet can only render placeholders.
  const awaitingNames = !catalogueQuery.data && (catalogueQuery.isPending || catalogueQuery.isFetching)

  const trackAnalytics = usePfRevampAnalytics()

  const handleEdit = useCallback((cartItemId: CartItemId) => {
    const item = items.find((i) => i.id === cartItemId)
    if (!item) return

    openForEdit(cartItemId, {
      shellProductId: item.shellProductId,
      variantId: item.variantId,
      optionValues: item.optionValues,
      extraServiceIds: item.extraServiceIds,
      extraQuantities: item.extraQuantities,
      quantity: item.quantity,
      note: item.note,
      // Re-opening a configured line must not silently reset its settings.
    })
    trackAnalytics({
      type: PfRevampEventName.PRODUCT_DRAWER_OPENED,
      payload: { shellProductId: item.shellProductId, cartItemId, mode: 'edit' },
    })
  }, [items, openForEdit, trackAnalytics])

  const { showConfirm } = useActionPopup()
  /** Past the catalogue, emptying the cart sends the client back to it, so that removal is confirmed first. */
  const confirmIfEmptying = useCallback(async (removedIds: CartItemId[]) => {
    if (navStep === 'catalogue' || items.some((item) => !removedIds.includes(item.id))) return true
    return !!await showConfirm(t('purchase_flow_revamp:cart.remove_last_body'), {
      title: t('purchase_flow_revamp:cart.remove_last_title'),
      confirmAcceptText: t('purchase_flow_revamp:cart.remove'),
      confirmDenyText: t('purchase_flow_revamp:cart.remove_cancel'),
    })
  }, [navStep, items, showConfirm, t])

  const handleRemove = useCallback(async (cartItemId: CartItemId) => {
    if (await confirmIfEmptying([cartItemId])) removeItem(cartItemId)
  }, [confirmIfEmptying, removeItem])

  const removePackLines = usePfStore((s) => s.removePack)
  const removePack = useCallback(async (selectionId: string) => {
    const packIds = items.filter((item) => item.pack?.selectionId === selectionId).map((item) => item.id)
    if (await confirmIfEmptying(packIds)) removePackLines(selectionId)
  }, [confirmIfEmptying, items, removePackLines])
  const entries = useMemo(() => groupRowsByPack(rows, items), [rows, items])
  const packName = useCallback((packProductCode: string) => {
    const shell = catalogueQuery.data?.bundles.find((b) => b.variants.some((v) => v.id === packProductCode))
    return shell?.displayName ?? packProductCode
  }, [catalogueQuery.data])

  // Only a line that carries a measurement configuration gets a breakdown; the
  // quote hook is real work and every other row would pay for it unused.
  const productConfigs = usePfStore((s) => s.productConfigs)
  const measurementRowIds = useMemo(
    () => new Set(rows.filter((row) => productConfigs[`${row.id}|measurement`]).map((row) => row.id)),
    [rows, productConfigs]
  )

  const isEmpty = rows.length === 0

  return (
    <>

      {/* Outer sticky container — fills column height so the bottom bar anchors
          to viewport bottom rather than flowing with page content. Phase C-B:
          the page-level sticky Back/Next footer is gone (StepShell no longer
          renders one), so the height calc is uniform across every step — no
          more conditional footer offset. */}
      <Stack
        data-cy="pf-revamp-cart-sidebar"
        sx={{
          position: isMobile ? undefined : 'sticky',
          top: isMobile ? undefined : PF_STICKY_TOP,
          height: isMobile ? undefined : `calc(100dvh - ${PF_STICKY_TOP})`,
          // In the phone sheet the sheet's body scrolls; the column fills it so the totals can sit at its foot.
          minHeight: isMobile ? '100%' : undefined,
          display: 'flex',
          flexDirection: 'column',
          // One rule separating the column from the page, instead of a box drawn
          // around the list. Desktop only — on mobile the cart lives inside the
          // drawer, which owns its own edge.
          borderLeft: isMobile ? undefined : `1px solid ${BEIGE_600}`,
          boxShadow: isMobile ? undefined : BOX_SHADOW_XS,
          px: isMobile ? undefined : 2.4,
        }}
      >

        {/* Fixed header — the title and the property it applies to stay put while
            the product list scrolls under them, so the summary never scrolls
            out of reach on a long cart. */}
        {/* On mobile the cart renders inside the ResponsiveDrawer, which
            already shows a "Summary" title — suppress the internal heading
            there to avoid a duplicate. Desktop sidebar keeps it (no drawer). */}
        {!isMobile &&
          <Typography
            variant="text-lg"
            fontWeight={550}
            color={GRAY_900}
            data-cy="pf-revamp-cart-summary-title"
            sx={{
              backgroundColor: BEIGE_300,
              borderBottom: `1px solid ${BEIGE_600}`,
              // Full-bleed across the column: cancel the column's padding so the
              // band meets the rule instead of floating inside it.
              mx: isMobile ? undefined : -2.4,
              px: isMobile ? 0 : 2.4,
              py: 1.6,
            }}
          >
            {t('purchase_flow_revamp:cart.summary_title')}
          </Typography>
        }

        {/* Scroll region — the product list only. The gap under the heading is
            padding INSIDE this box, not a margin under the band, so the list
            scrolls straight up beneath the band instead of past a white strip
            that stays behind. */}
        <Stack
          gap={1.2}
          sx={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
            pt: 1.6,
            pb: 1.2,
          }}
        >

          {/* In the embed the CRM already shows the property the order is placed from, so the address only takes
              room from the products; the chosen creative still shows. */}
          {!!property && (!isEmbed || !!ctBookingName) &&
            <Stack
              direction="row"
              alignItems="flex-start"
              gap={0.8}
              data-cy="pf-revamp-cart-property-indicator"
              sx={{
                backgroundColor: BEIGE_500,
                border: `1px solid ${BEIGE_700}`,
                borderRadius: '0.8rem',
                px: 1.6,
                py: 1.2,
              }}
            >

              {!isEmbed && <HomeOutlinedIcon sx={{ fontSize: '1.6rem', color: GRAY_700, mt: '0.2rem', flexShrink: 0 }} />}

              <Stack gap={0.2} minWidth={0}>

                {!isEmbed &&
                  <>
                    <Typography variant="text-sm" color={GRAY_700} sx={{ lineHeight: 1.5 }}>
                      {t('purchase_flow_revamp:cart.property_indicator_prefix')}
                    </Typography>

                    {/* Its own row — a long address wrapped mid-sentence and read
                        as part of the label rather than as the property. */}
                    <Typography variant="text-sm" fontWeight={700} color={GRAY_900} sx={{ lineHeight: 1.5 }}>
                      {property.addressLabel}
                    </Typography>
                  </>
                }

                {!!ctBookingName &&
                  <Typography variant="text-sm" color={GRAY_700} sx={{ lineHeight: 1.5, mt: isEmbed ? 0 : 0.4 }} data-cy="pf-revamp-cart-selected-creative">
                    {`${t('purchase_flow_revamp:instructions.schedule.selected_creative_label')}: `}
                    <Typography component="span" variant="text-sm" fontWeight={700} color={GRAY_900}>{ctBookingName}</Typography>
                  </Typography>
                }

              </Stack>

            </Stack>
          }

          {isEmpty &&
            <Box
              sx={{
                backgroundColor: WHITE,
                border: `1px solid ${BEIGE_600}`,
                borderRadius: '0.8rem',
                px: 1.6,
                py: 1.6,
                textAlign: 'center',
              }}
            >
              <Typography variant="text-sm" color={GRAY_500}>
                {t('purchase_flow_revamp:cart.select_first_product')}
              </Typography>
            </Box>
          }

          {/* A resumed draft has its lines before the catalogue that names them:
              `useCartDisplay` can only fall back to "Unknown product" until it
              arrives. Wait rather than show that. */}
          {!isEmpty && awaitingNames &&
            <Stack alignItems="center" py={4} data-cy="pf-revamp-cart-loading">
              <CircularProgress size="2.4rem" />
            </Stack>
          }

          {!isEmpty && !awaitingNames &&
            <Stack gap={0.8}>
              {entries.map((entry) => {
                const card = (row: typeof rows[number], locked = isPaymentPending) => (
                  <CartItemCard
                    key={row.id}
                    row={row}
                    onEdit={handleEdit}
                    onRemove={handleRemove}
                    locked={locked}
                    isActive={!!activeCartItemId && row.id === activeCartItemId}
                    // The measurement line's price is a sum of engine-derived
                    // carriers; on its own it has no visible cause.
                    breakdownSlot={measurementRowIds.has(row.id) && <MeasurementBreakdown cartItemId={row.id} />}
                  />
                )
                if (entry.kind === 'line') return card(entry.row)
                const priced = entry.rows.every((row) => row.lineTotal && row.currency)
                const total = entry.rows.reduce((sum, row) => sum.plus(row.lineTotal ?? 0), new Big(0))
                return (
                  <PackCartGroup
                    key={entry.selectionId}
                    selectionId={entry.selectionId}
                    name={packName(entry.packProductCode)}
                    totalLabel={priced ? formatPrice(total, entry.rows[0].currency!) : null}
                    onRemove={removePack}
                    removable={!isPaymentPending}
                  >
                    {entry.rows.map((row) => card(row, true))}
                  </PackCartGroup>
                )
              })}
            </Stack>
          }

          {/* V-16 / QA-654 — BE-derived charges that are inside the total but are not cart items (travel fees,
              access fees, extras), listed as lines of their own so the visible lines reconcile with the total.
              Shown while a resumed draft's names load too, since the total below already includes them. */}
          {!isEmpty && extraRows.length > 0 &&
            <Stack gap={0.8}>
              {extraRows.map((extra) => <CartExtraCard key={extra.kind} extra={extra} currency={currency} />)}
            </Stack>
          }

        </Stack>

        {/* Pinned bottom bar — totals + per-step nav (Phase C-B).
            On mobile the cart renders inside MobileBottomBar's sheet, and the
            fixed footer owns the Back/continue nav — so the sheet shows totals
            only (no duplicate nav). Desktop keeps the stacked sidebar nav. */}
        {(!isEmpty || !isMobile) &&
          <Stack
            ref={isMobile ? undefined : footerRef}
            data-cy="pf-revamp-cart-footer"
            gap={1.2}
            // pb keeps the CTA off the bottom of the page. In the phone sheet the totals stay pinned to its bottom
            // edge while the items scroll; the negative margin cancels the sheet's own bottom padding.
            sx={isMobile
              ? { pb: '2rem', mb: '-2rem', mt: 'auto', position: 'sticky', bottom: '-2rem', backgroundColor: WHITE }
              : { pb: 1.2 }}
          >

            {!isEmpty &&
              <CartSummary total={total} currency={currency} discountPercentage={catalogueQuery.data?.discountPercentage} paymentMethod={paymentMethod} vat={vatQuery.data} oneSystem={oneSystem} chargedAmount={chargedAmount} />
            }

            {!isMobile &&
              <StepNavSection step={navStep} layout="sidebar" />
            }

          </Stack>
        }

      </Stack>

    </>
  )
}
